/**
 * azure-functions/shared/state/obligationMigration.cjs
 *
 * Canonical (GH #7115 pattern): shared by the browser, which re-exports it from src/, and the Function App, so the server can run the exact load path the browser runs (measurement-parity-diff). Edit this file, never the src/ re-export.
 *
 * Idempotent migration functions for the obligation model (Phase 1).
 *
 * Called from migrateState() in scfMigration.js on every state load.
 * Safe to run repeatedly — each function checks preconditions before mutating.
 */

'use strict';

const { OBLIGATIONS_BY_ID } = require('../compliance/obligations/index.cjs');

// ── Migration 1: adoptedStandardTasks → enabledObligations ──────────────────

/**
 * Migrate adopted standard tasks to enabled obligations.
 * Only runs if enabledObligations is empty and adoptedStandardTasks has entries.
 *
 * @param {object} state - Tenant state (mutated in place)
 * @returns {object} state
 */
function migrateAdoptedToObligations(state) {
  const adopted = state.adoptedStandardTasks || [];
  const existing = state.enabledObligations || [];

  if (adopted.length === 0 || existing.length > 0) return state;

  state.enabledObligations = adopted.map((a) => {
    const oblId = (a.standardTaskId || '').replace('std-', 'obl-');
    const def = OBLIGATIONS_BY_ID.get(oblId);

    // Determine if the adopted frequency differs from the template default
    let cadenceOverride;
    if (def && a.frequency && a.frequency !== def.cadence.frequency) {
      cadenceOverride = { frequency: a.frequency };
    }

    return {
      obligationId: oblId,
      enabledAt: a.adoptedAt || new Date().toISOString(),
      enabledBy: 'migration',
      cadenceOverride,
      assigneeId: a.assigneeId || null,
      enabled: a.enabled !== false,
    };
  });

  return state;
}

// ── Migration 2: task evidence → artefacts ──────────────────────────────────

const EVIDENCE_TYPE_MAP = {
  file: 'file',
  structured: 'report',
  // 'comment' and 'auto-close' are skipped — not artefacts
};

/**
 * Migrate evidence from existing tasks into the standalone artefacts array.
 * Only runs if artefacts is empty and tasks have evidence entries.
 *
 * Migrates only 'file' and 'structured' evidence — skips comments and auto-close.
 * Populates controlIds from the task's controls array and domainRefs from task
 * source metadata (documentId, riskId, sourceType). Generates artefact title
 * from evidence filename (files) or template name (structured).
 *
 * @param {object} state - Tenant state (mutated in place)
 * @returns {object} state
 */
function migrateTaskEvidence(state) {
  const artefacts = state.artefacts || [];
  const tasks = state.tasks || [];

  if (artefacts.length > 0) return state;

  const migrated = [];

  for (const task of tasks) {
    if (task.sourceType === 'manual') continue;
    if (!task.evidence || task.evidence.length === 0) continue;

    for (const ev of task.evidence) {
      const artefactType = EVIDENCE_TYPE_MAP[ev.type];
      if (!artefactType) continue; // skip comment, auto-close

      migrated.push({
        id: `art-${ev.id || crypto.randomUUID?.() || Date.now()}`,
        title: buildArtefactTitle(ev, task),
        type: artefactType,
        filename: ev.filename || null,
        storagePath: ev.storagePath || null,
        mimeType: ev.mimeType || null,
        fileSize: ev.fileSize || null,
        description: ev.content || '',
        controlIds: Array.isArray(task.controls) ? [...task.controls] : [],
        domainRefs: buildDomainRefs(task),
        uploadedAt: ev.addedAt || ev.createdAt || new Date().toISOString(),
        uploadedBy: ev.addedBy || null,
        uploadedByName: ev.addedByName || null,
        migratedFrom: { taskId: task.id, evidenceId: ev.id },
      });
    }
  }

  if (migrated.length > 0) {
    state.artefacts = migrated;
  }

  return state;
}

/**
 * Generate a human-readable title for a migrated artefact.
 * Files → filename; structured → template name; fallback → task title.
 */
function buildArtefactTitle(ev, task) {
  if (ev.type === 'file' && ev.filename) return ev.filename;
  if (ev.type === 'structured' && ev.templateName) return ev.templateName;
  return ev.label || ev.title || task.title || 'Untitled artefact';
}

/**
 * Build domainRefs from a task's source metadata.
 * Checks documentId, riskId, vendorId, employeeId, and generic sourceType/sourceId.
 */
function buildDomainRefs(task) {
  const refs = [];
  if (task.documentId) refs.push({ type: 'document', id: task.documentId });
  if (task.riskId) refs.push({ type: 'risk', id: task.riskId });
  if (task.vendorId) refs.push({ type: 'vendor', id: task.vendorId });
  if (task.employeeId) refs.push({ type: 'employee', id: task.employeeId });
  // Catch generic sourceType/sourceId pairs not covered above
  if (task.sourceId && task.sourceType && !refs.some((r) => r.id === task.sourceId)) {
    refs.push({ type: task.sourceType, id: task.sourceId });
  }
  return refs;
}

// ── Migration 3: state.tasks → state.adhocTasks (ad-hoc segregation) ───────

const ADHOC_SOURCE_TYPES = new Set(['manual', 'gap-analysis', 'bia']);

/**
 * Segregate manual / gap-analysis / BIA tasks into state.adhocTasks[].
 * Only runs if adhocTasks is empty and state.tasks has qualifying entries.
 *
 * Marks each migrated task with `tier: 'adhoc'`.
 * Does NOT delete from state.tasks yet — Phase 8 cleanup handles that.
 *
 * Idempotent — skips if adhocTasks is already populated.
 *
 * @param {object} state - Tenant state (mutated in place)
 * @returns {object} state
 */
function migrateAdhocTasks(state) {
  if ((state.adhocTasks || []).length > 0) return state; // already migrated
  if (!state.tasks || state.tasks.length === 0) return state;

  state.adhocTasks = state.tasks
    .filter((t) => ADHOC_SOURCE_TYPES.has(t.sourceType) || !t.sourceType)
    .map((t) => ({
      ...t,
      tier: 'adhoc',
    }));

  return state;
}

// ── Migration 4: risk.tasks → risk.actions (DomainAction shape) ────────────

/**
 * Migrate risk tasks to domain-backed actions.
 *
 * For each risk:
 * 1. Convert embedded risk.tasks[] to risk.actions[] (DomainAction shape)
 * 2. Pull in orphaned tasks from state.tasks[] that have sourceType 'risk'
 * 3. Remove risk-sourced tasks from state.tasks[]
 *
 * Idempotent — skips risks that already have an actions array.
 *
 * @param {object} state - Tenant state (mutated in place)
 * @returns {object} state
 */
function migrateRiskActions(state) {
  if (!state.risks) return state;
  const tasks = state.tasks || [];

  for (const risk of state.risks) {
    // Convert embedded risk.tasks to risk.actions if not already done
    if (risk.tasks && !risk.actions) {
      risk.actions = (risk.tasks || []).map((rt) => ({
        id: rt.id,
        title: rt.title,
        description: rt.description || '',
        status: 'pending',
        assigneeId: rt.assigneeId || '',
        dueDate: rt.dueDate || null,
        createdAt: rt.createdAt || new Date().toISOString(),
        completedAt: null,
        completedBy: null,
        evidenceRefs: [],
      }));
    }
    if (!risk.actions) risk.actions = [];

    // Pull in any tasks from state.tasks that were created for this risk
    const riskTasks = tasks.filter((t) => t.riskId === risk.id && t.sourceType === 'risk');
    for (const rt of riskTasks) {
      const alreadyExists = risk.actions.some((a) => a.title === rt.title);
      if (!alreadyExists) {
        risk.actions.push({
          id: rt.id,
          title: rt.title,
          description: rt.description || '',
          status:
            rt.status === 'closed'
              ? 'completed'
              : rt.status === 'in-progress'
                ? 'in-progress'
                : 'pending',
          assigneeId: rt.assigneeId || '',
          dueDate: rt.dueDate || null,
          createdAt: rt.createdAt || new Date().toISOString(),
          completedAt: rt.status === 'closed' ? rt.closedAt || new Date().toISOString() : null,
          completedBy: rt.status === 'closed' ? rt.closedBy || 'migration' : null,
          evidenceRefs: (rt.evidence || [])
            .filter((e) => e.type === 'file' || e.type === 'structured')
            .map((e) => ({
              description: e.filename || e.templateName || e.content?.slice(0, 100),
              timestamp: e.createdAt,
            })),
        });
      }
    }
  }

  // Remove risk-sourced tasks from state.tasks
  state.tasks = tasks.filter((t) => t.sourceType !== 'risk');
  return state;
}

module.exports = {
  migrateAdoptedToObligations,
  migrateTaskEvidence,
  migrateAdhocTasks,
  migrateRiskActions,
};
